import { useRef, useEffect, useState, useCallback } from "react";

interface EmailBodyIframeProps {
  html: string;
  maxCollapsedHeight?: number;
  isExpanded?: boolean;
  onOverflowChange?: (isOverflowing: boolean) => void;
}

const EmailBodyIframe = ({
  html,
  maxCollapsedHeight = 68,
  isExpanded = false,
  onOverflowChange,
}: EmailBodyIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const adjustHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;

    const body = doc.body;
    const docEl = doc.documentElement;
    const height = Math.max(
      body?.scrollHeight || 0,
      body?.offsetHeight || 0,
      docEl?.clientHeight || 0,
      docEl?.scrollHeight || 0,
      docEl?.offsetHeight || 0
    );
    setContentHeight(height);
    onOverflowChange?.(height > maxCollapsedHeight);
  }, [maxCollapsedHeight, onOverflowChange]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !html) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const wrappedHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            html, body {
              margin: 0;
              padding: 0;
              background: white;
              color: #000;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              word-break: break-word;
              overflow-wrap: anywhere;
            }
            img { max-width: 100%; height: auto; }
            table { max-width: 100%; overflow-x: auto; }
            p { margin-bottom: 8px; }
            p:last-child { margin-bottom: 0; }
          </style>
        </head>
        <body>${html}</body>
      </html>
    `;

    doc.open();
    doc.write(wrappedHtml);
    doc.close();

    setTimeout(adjustHeight, 100);

    const resizeHandler = adjustHeight;
    iframe.contentWindow?.addEventListener("resize", resizeHandler);

    const images = doc.querySelectorAll("img");
    const handleImageLoad = () => setTimeout(adjustHeight, 50);
    images.forEach((img) => img.addEventListener("load", handleImageLoad));

    return () => {
      iframe.contentWindow?.removeEventListener("resize", resizeHandler);
      images.forEach((img) => img.removeEventListener("load", handleImageLoad));
    };
  }, [html, adjustHeight]);

  const displayHeight = isExpanded
    ? contentHeight
    : Math.min(contentHeight, maxCollapsedHeight);

  if (!html) return null;

  return (
    <iframe
      ref={iframeRef}
      className="w-full border-0 overflow-hidden"
      style={{ height: displayHeight ? `${displayHeight}px` : "auto" }}
      sandbox="allow-same-origin"
      title="Email content"
    />
  );
};

export default EmailBodyIframe;
